import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatNativeDateModule } from '@angular/material/core';
import { ApiService } from '../../core/api.service';
import { SocketService } from '../../core/socket.service';
import { DialogService } from '../../shared/dialog.service';
import { FeedbackService } from '../../shared/feedback.service';
import { hasValue, sanitizeText, validateValue } from '../../shared/validation';

@Component({
  selector: 'app-terminal',
  standalone: true,
  imports: [FormsModule, NgFor, NgIf, NgClass, MatDatepickerModule, MatFormFieldModule, MatInputModule, MatNativeDateModule],
  templateUrl: './terminal.component.html',
  styleUrl: './terminal.component.scss'
})
export class TerminalComponent implements OnInit {
  queue: any[] = [];
  selected: any;
  dateFilterDate = new Date();
  message = 'Loading terminal queue...';
  rejectionReasons = ['Material Defect', 'Machine Issue', 'Dimension Mismatch', 'Surface Damage', 'Assembly Error', 'Quality Check Failed'];
  form = { producedQuantity: 0, rejectedQuantity: 0, rejectionReason: '', reason: '' };
  error = '';
  success = '';
  loading = false;
  productionAttemptedSubmit = false;

  constructor(private socket: SocketService, private api: ApiService, private dialog: DialogService, private feedback: FeedbackService) {}

  /**
   * Loads the terminal queue and subscribes to realtime production/attendance updates.
   * @returns void.
   */
  ngOnInit() {
    this.loadQueue();
    this.socket.on('attendance:event-imported').subscribe(() => this.loadQueue());
    this.socket.on('production:submitted').subscribe(() => this.loadQueue());
    this.socket.on('attendance:updated').subscribe(() => this.loadQueue());
    this.socket.on('assignment:updated').subscribe(() => this.loadQueue());
  }

  /**
   * Fetches terminal records for the selected date and selects the next actionable item.
   * @returns void.
   */
  loadQueue() {
    this.loading = true;
    this.api.get<any[]>(`/attendance-events/terminal-queue?date=${this.toDateInput(this.dateFilterDate)}`).subscribe({
      next: (rows) => {
        this.queue = rows;
        this.selected = this.pickNext(rows);
        this.message = rows.length ? `${rows.length} terminal item(s) for selected date` : 'No assignment found. Please contact supervisor.';
        this.resetFormFromSelected();
        this.loading = false;
      },
      error: (error) => {
        this.error = error.error?.message || 'Unable to load terminal queue';
        this.feedback.error(this.error);
        this.loading = false;
      }
    });
  }

  /**
   * Selects a terminal queue item and resets the production form from existing data.
   * @param item Terminal queue record.
   * @returns void.
   */
  select(item: any) {
    this.selected = item;
    this.success = '';
    this.error = '';
    this.resetFormFromSelected();
  }

  /**
   * Validates and submits production quantities for the selected terminal assignment.
   * @returns Promise resolved after confirmation flow is complete.
   */
  async submitProduction() {
    this.productionAttemptedSubmit = true;
    this.error = '';
    this.success = '';
    const assignment = this.selected?.assignment;
    if (!assignment?._id) {
      this.error = 'No assignment is available for this queue item.';
      return;
    }
    this.form.reason = sanitizeText(this.form.reason);
    const producedMessage = validateValue('Produced Quantity', this.form.producedQuantity, { required: true, kind: 'number', min: 0, max: 1000000, integer: true });
    const rejectedMessage = validateValue('Rejected Quantity', this.form.rejectedQuantity, { required: true, kind: 'number', min: 0, max: 1000000, integer: true });
    if (producedMessage || rejectedMessage) {
      this.error = producedMessage || rejectedMessage;
      return;
    }
    const produced = Number(this.form.producedQuantity);
    const rejected = Number(this.form.rejectedQuantity);
    if (rejected <= 0) this.form.rejectionReason = '';
    const target = Number(assignment.quantityToProduce || 0);
    if (produced + rejected > target) {
      this.error = 'Produced + rejected quantity cannot exceed target quantity.';
      return;
    }
    if (produced + rejected !== target && !hasValue(this.form.reason)) {
      this.error = 'Reason is required when produced + rejected quantity does not equal target.';
      return;
    }
    if (rejected > 0 && !this.form.rejectionReason) {
      this.error = 'Rejection reason is required when rejected quantity is greater than 0.';
      return;
    }
    const confirmation = await this.dialog.confirm({
      title: 'Submit production entry',
      message: 'Confirm production quantities before submission.',
      confirmText: 'Submit'
    });
    if (!confirmation.confirmed) return;

    this.api
      .post('/attendance-events/terminal-production', {
        assignment: assignment._id,
        producedQuantity: this.form.producedQuantity,
        rejectedQuantity: this.form.rejectedQuantity,
        rejectionReason: this.form.rejectionReason,
        reason: this.form.reason
      })
      .subscribe({
        next: () => {
          this.success = 'Production entry completed. Moving to next item.';
          this.feedback.success(this.success);
          this.loadQueue();
        },
        error: (error) => {
          this.error = error.error?.message || 'Production entry failed';
          this.feedback.error(this.error);
        }
      });
  }

  /** Opens the browser print dialog for the selected terminal job. */
  printSelected() {
    window.print();
  }

  /**
   * Formats terminal timestamps for display.
   * @param value Date string.
   * @returns Localized date/time label.
   */
  formatDate(value: string) {
    return value ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(value)) : '';
  }

  /**
   * Determines whether a queue item has checked out but still needs production entry.
   * @param item Queue item, defaulting to current selection.
   * @returns True when production entry is required.
   */
  needsProduction(item = this.selected) {
    return item?.workflowState === 'Ready For Out' && !item?.productionEntry;
  }

  /**
   * Returns a user-facing terminal status for the selected queue item.
   * @param item Queue item.
   * @returns Status label.
   */
  statusLabel(item: any) {
    if (!item.assignment) return 'No assignment found';
    if (item.productionEntry) return 'Production entry completed';
    if (item.workflowState === 'Awaiting Action') return 'Choose Break or Out';
    if (item.workflowState === 'Ready For Out') return 'Out selected - production required';
    if (item.workflowState === 'On Break') return 'Break out recorded';
    if (item.workflowState === 'Break Completed') return 'Break completed';
    if (item.checkOutEvent) return 'Checked out';
    return 'Checked in - job active';
  }

  /**
   * Records the next terminal workflow action for the selected attendance record.
   * @param action Break out, break in, or request final out.
   * @returns void.
   */
  terminalAction(action: 'breakOut' | 'breakIn' | 'requestOut') {
    if (!this.selected?._id) return;
    this.error = '';
    this.success = '';
    this.api.post('/attendance-events/terminal-action', { recordId: this.selected._id, action }).subscribe({
      next: () => {
        this.success = action === 'requestOut' ? 'Production entry is required before final out.' : 'Punch action recorded.';
        this.feedback.success(this.success);
        this.loadQueue();
      },
      error: (error) => {
        this.error = error.error?.message || 'Unable to record terminal action';
        this.feedback.error(this.error);
      }
    });
  }

  canBreakOut(item = this.selected) {
    return Boolean(item?.assignment) && item?.workflowState === 'Awaiting Action' && Boolean(item?.pendingActionEvent) && !item?.productionEntry;
  }

  canBreakIn(item = this.selected) {
    return Boolean(item?.assignment) && item?.workflowState === 'On Break';
  }

  canRequestOut(item = this.selected) {
    return Boolean(item?.assignment) && item?.workflowState === 'Awaiting Action' && Boolean(item?.pendingActionEvent) && !item?.productionEntry;
  }

  /**
   * Chooses the next queue item that needs production work.
   * @param rows Terminal queue records.
   * @returns Selected queue item or null.
   */
  private pickNext(rows: any[]) {
    return rows.find((row) => this.needsProduction(row)) || rows.find((row) => this.canBreakIn(row)) || rows.find((row) => !row.productionEntry) || rows[0] || null;
  }

  /**
   * Copies an existing production entry into the terminal form when present.
   * @returns void.
   */
  private resetFormFromSelected() {
    const entry = this.selected?.productionEntry;
    this.form = {
      producedQuantity: entry?.producedQuantity || 0,
      rejectedQuantity: entry?.rejectedQuantity || 0,
      rejectionReason: entry?.rejectionReason || '',
      reason: entry?.reason || ''
    };
    this.productionAttemptedSubmit = false;
  }

  /** Returns whether the rejection reason field should be displayed. */
  showRejectionReason() {
    return Number(this.form.rejectedQuantity || 0) > 0;
  }

  productionFieldError(field: 'producedQuantity' | 'rejectedQuantity' | 'reason' | 'rejectionReason') {
    if (field === 'producedQuantity') return validateValue('Produced Quantity', this.form.producedQuantity, { required: true, kind: 'number', min: 0, max: 1000000, integer: true });
    if (field === 'rejectedQuantity') return validateValue('Rejected Quantity', this.form.rejectedQuantity, { required: true, kind: 'number', min: 0, max: 1000000, integer: true });
    if (field === 'rejectionReason' && this.showRejectionReason()) return validateValue('Rejection Reason', this.form.rejectionReason, { required: true, kind: 'select' });
    if (field === 'reason') return validateValue('Reason', this.form.reason, { maxLength: 300 });
    return '';
  }

  showProductionFieldError(field: 'producedQuantity' | 'rejectedQuantity' | 'reason' | 'rejectionReason') {
    return this.productionAttemptedSubmit && Boolean(this.productionFieldError(field));
  }

  /**
   * Converts a Date to an HTML date-input value.
   * @param value Date to format.
   * @returns yyyy-mm-dd string.
   */
  private toDateInput(value: Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
